/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Why Choose FalcoCut section with expandable features
 */
import Image from 'next/image';
import React, { useRef, useState } from 'react';

interface Feature {
  id: string;
  iconUrl: string;
  title: string;
  description: string;
  imageUrl: string;
}

interface FeatureItemProps {
  feature: Feature;
  isExpanded: boolean;
  onClick: () => void;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ feature, isExpanded, onClick }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  React.useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [feature.description]);

  return (
    <div
      className={`
        rounded-xl overflow-hidden transition-all duration-500 ease-in-out cursor-pointer
        ${isExpanded ? 'bg-[#F8F8F8]' : 'bg-transparent hover:bg-white/5'}
      `}
      onClick={onClick}
    >
      <div className="p-4 pad:p-5 sm:p-6">
        {/* 标题行 */}
        <div className="flex items-center gap-3 pad:gap-4">
          {/* Icon */}
          <div className="flex-shrink-0">
            <Image
              src={feature.iconUrl}
              alt=""
              width={32}
              height={32}
              className="object-contain"
              unoptimized
            />
          </div>

          {/* 标题 */}
          <h3 className="flex-1 text-base pad:text-lg sm:text-xl font-bold">
            {feature.title}
          </h3>

          {/* 展开/收起箭头 */}
          <div
            className={`
              flex-shrink-0 w-6 h-6 flex items-center justify-center
              transition-transform duration-500 ease-in-out
              ${isExpanded ? 'rotate-180' : ''}
            `}
          >
            <svg
              className="w-4 h-4 text-[#333333]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* 描述 - 展开时显示 */}
        <div
          style={{
            maxHeight: isExpanded ? `${contentHeight}px` : '0px',
            marginTop: isExpanded ? '12px' : '0px',
          }}
          className="overflow-hidden transition-all duration-500 ease-in-out"
        >
          <div ref={contentRef}>
            <p className="text-sm pad:text-base text-[#333333] leading-relaxed pl-11 pad:pl-12">
              {feature.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const features: Feature[] = [
    {
      id: 'fast',
      iconUrl: '/images/landing-icon-fast-and-cost-effective.png',
      title: 'Fast & Cost-Effective',
      description:
        'Translate videos in minutes with AI precision. FalcoCut ensures your subtitles, dubbing, and lip sync stay natural and aligned, delivering professional results without the long wait.',
      imageUrl: '/images/landing-fast-and-cost-effective.png',
    },
    {
      id: 'global',
      iconUrl: '/images/landing-icon-reach-global-audiences.png',
      title: 'Reach Global Audiences',
      description:
        "Expand your video's impact across 35+ languages. From YouTube and TikTok to corporate training, FalcoCut helps you connect with international viewers and grow your audience worldwide.",
      imageUrl: '/images/landing-reach-global-audiences.png',
    },
    {
      id: 'voice',
      iconUrl: '/images/landing-icon-preserve-authentic-voices.png',
      title: 'Preserve Authentic Voices',
      description:
        "FalcoCut's voice cloning technology maintains your unique tone and speaking style, so your translated videos still sound like you—just in another language.",
      imageUrl: '/images/landing-preserve-authentic-voices.png',
    },
    {
      id: 'lipsync',
      iconUrl: '/images/landing-icon-perfect-lip-sync.png',
      title: 'Perfect Lip Sync',
      description:
        'Our AI dubbing aligns translated voices with lip movements for a natural viewing experience. No mismatched timing—your content feels truly native in every market.',
      imageUrl: '/images/landing-perfect-lip-sync.png',
    },
    {
      id: 'subtitle',
      iconUrl: '/images/landing-icon-reach-global-audiences.png',
      title: 'Customizable Subtitles & Branding',
      description:
        'Easily generate accurate subtitles and style them to match your brand. Adjust fonts, colors, and placement to create a consistent, polished look for any platform.',
      imageUrl: '/images/landing-customizable-subtitles-and-branding.png',
    },
    {
      id: 'platform',
      iconUrl: '/images/landing-icon-perfect-lip-sync.png',
      title: 'All-in-One Localization Platform',
      description:
        'FalcoCut brings subtitles, dubbing, transcription, and exporting together in one intuitive tool. No need for multiple apps—everything you need to localize videos is in one place.',
      imageUrl: '/images/landing-all-in-one-localization-platform.png',
    },
  ];

  const handleFeatureClick = (index: number) => {
    setExpandedIndex(index);
  };

  const currentImage = features[expandedIndex].imageUrl;

  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-10 pad:mb-12 sm:mb-16">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
            Why Choose FalcoCut AI Video Translator?
          </h2>
          <p className="text-sm pad:text-base text-gray-600 max-w-4xl mx-auto">
            FalcoCut isn&apos;t just another video translator—it&apos;s an all-in-one AI
            platform that makes video translation faster, smarter, and more authentic.
            From subtitles and dubbing to voice cloning and lip sync, we help creators,
            educators, and businesses localize content in 35 languages with ease.
          </p>
        </div>

        {/* 左右布局 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pad:gap-10 sm:gap-12">
          {/* 左侧图片 - 桌面端 */}
          <div className="hidden sm:flex sm:items-center">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-100 to-blue-100 w-full">
              <div className="aspect-[4/3] relative">
                <Image
                  key={currentImage}
                  src={currentImage}
                  alt={features[expandedIndex].title}
                  fill
                  className="transition-opacity duration-500"
                  objectFit="contain"
                />
              </div>
            </div>
          </div>

          {/* 右侧功能列表 */}
          <div className="space-y-2 pad:space-y-3">
            {features.map((feature, index) => (
              <FeatureItem
                key={feature.id}
                feature={feature}
                isExpanded={expandedIndex === index}
                onClick={() => handleFeatureClick(index)}
              />
            ))}
          </div>

          {/* 移动端图片 - 显示在列表下方 */}
          <div className="sm:hidden">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-100 to-blue-100">
              <div className="aspect-[4/3] relative">
                <Image
                  key={currentImage}
                  src={currentImage}
                  alt={features[expandedIndex].title}
                  fill
                  className="object-cover transition-opacity duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
